import React, { useMemo, useRef, useState } from 'react';
import { styled, keyframes } from '@mui/system';

const eyeBlink = keyframes`
  0%,
  92%,
  100% {
    animation-timing-function: cubic-bezier(0.32, 0, 0.67, 0);
    height: 0.75em;
  }

  96% {
    animation-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
    height: 0;
  }
`;

const CreepyButtonRoot = styled('button')(({ theme }) => ({
  border: 0,
  boxSizing: 'border-box',
  margin: 0,
  padding: 0,

  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',

  // Defaults aligned with our "traditional" MUI button styling,
  // with optional per-instance overrides via CSS variables.
  '--creepy-btn-font-size': '18px',
  '--creepy-btn-line-height': '22px',
  '--creepy-btn-font-weight': 500,
  '--creepy-btn-pad-y': '14px',
  '--creepy-btn-pad-left': '33px',
  '--creepy-btn-pad-right': '33px',
  '--creepy-btn-pad-right-hover': '56px',

  fontFamily: 'Rubik-Regular, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
  fontSize: 'var(--creepy-btn-font-size, 18px)',
  lineHeight: 'var(--creepy-btn-line-height, 22px)',
  fontWeight: 'var(--creepy-btn-font-weight, 500)',
  textTransform: 'none',

  // This base stays hidden until the cover rotates on hover/focus.
  backgroundColor: theme.palette?.black?.main ?? '#000',
  borderRadius: 0,
  cursor: 'pointer',
  letterSpacing: 'normal',

  outline: '0.1875em solid transparent',
  transition: 'outline 0.1s linear',
  WebkitTapHighlightColor: 'transparent',

  '& .CreepyButton-cover': {
    position: 'relative',
    inset: 0,
    display: 'block',
    width: '100%',
    flex: 1,
    borderRadius: 'inherit',
    textAlign: 'center',
    paddingTop: 'var(--creepy-btn-pad-y, 14px)',
    paddingBottom: 'var(--creepy-btn-pad-y, 14px)',
    paddingLeft: 'var(--creepy-btn-pad-left, 33px)',
    paddingRight: 'var(--creepy-btn-pad-right, 33px)',

    backgroundColor: 'rgba(185, 248, 51, 1)',
    color: theme.palette.textPrimary.main,

    transformOrigin: '1.25em 50%',
    transition: `background-color 0.3s, padding-right 0.3s, transform 0.3s cubic-bezier(0.65, 0, 0.35, 1)`,
  },

  '& .CreepyButton-eyes': {
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375em',
    right: '1em',
    bottom: '0.5em',
    height: '0.75em',
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.2s linear',
  },

  '& .CreepyButton-eye': {
    position: 'relative',
    animation: `${eyeBlink} 3s infinite`,
    backgroundColor: theme.palette?.grey?.[50] ?? '#f5f5f5',
    borderRadius: '50%',
    overflow: 'hidden',
    width: '0.75em',
    height: '0.75em',
  },

  '& .CreepyButton-pupil': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0.375em',
    aspectRatio: '1 / 1',
    borderRadius: 'inherit',
    backgroundColor: theme.palette?.black?.main ?? '#000',
    transform: 'translate(-50%, -50%)',
  },

  '&:focus-visible': {
    outline: `0.1875em solid ${theme.palette?.primary?.bright ?? theme.palette?.primary?.main ?? '#88F200'}`,
  },

  '&:hover .CreepyButton-cover': {
    backgroundColor: theme.palette?.primary?.main ?? 'rgba(121, 201, 18, 1)',
    paddingRight: 'var(--creepy-btn-pad-right-hover, 56px)',
  },

  '&:focus-visible .CreepyButton-eyes, &:hover .CreepyButton-eyes': {
    opacity: 1,
  },

  '&:focus-visible .CreepyButton-cover, &:hover .CreepyButton-cover': {
    transform: 'rotate(-12deg)',
    transitionTimingFunction: 'cubic-bezier(0.65, 0, 0.35, 1.65)',
  },

  '&:active .CreepyButton-cover': {
    transform: 'rotate(0)',
    transitionTimingFunction: 'cubic-bezier(0.65, 0, 0.35, 1)',
  },

  [theme.breakpoints.down('sm')]: {
    '--creepy-btn-pad-left': '27px',
  },
}));

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export default function CreepyButton({ onClick, children, className, type = 'button', ...rest }) {
  const eyesRef = useRef(null);
  const [eyeCoords, setEyeCoords] = useState({ x: 0, y: 0 });

  const eyeStyle = useMemo(() => {
    const translateX = `${-50 + eyeCoords.x * 50}%`;
    const translateY = `${-50 + eyeCoords.y * 50}%`;
    return { transform: `translate(${translateX}, ${translateY})` };
  }, [eyeCoords.x, eyeCoords.y]);

  const updateEyes = (e) => {
    const userEvent = 'touches' in e ? e.touches?.[0] : e;
    if (!userEvent || !eyesRef.current) return;

    const eyesRect = eyesRef.current.getBoundingClientRect();
    const eyesCenter = {
      x: eyesRect.left + eyesRect.width / 2,
      y: eyesRect.top + eyesRect.height / 2,
    };

    const cursor = { x: userEvent.clientX, y: userEvent.clientY };
    const dx = cursor.x - eyesCenter.x;
    const dy = cursor.y - eyesCenter.y;

    const angle = Math.atan2(-dy, dx) + Math.PI / 2;
    const visionRangeX = 180;
    const visionRangeY = 75;
    const distance = Math.hypot(dx, dy);

    const x = clamp((Math.sin(angle) * distance) / visionRangeX, -1, 1);
    const y = clamp((Math.cos(angle) * distance) / visionRangeY, -1, 1);
    setEyeCoords({ x, y });
  };

  const resetEyes = () => setEyeCoords({ x: 0, y: 0 });

  return (
    <CreepyButtonRoot
      className={className}
      type={type}
      onClick={onClick}
      onMouseMove={updateEyes}
      onMouseLeave={resetEyes}
      onTouchMove={updateEyes}
      onTouchEnd={resetEyes}
      {...rest}
    >
      <span className="CreepyButton-eyes" ref={eyesRef}>
        <span className="CreepyButton-eye">
          <span className="CreepyButton-pupil" style={eyeStyle} />
        </span>
        <span className="CreepyButton-eye">
          <span className="CreepyButton-pupil" style={eyeStyle} />
        </span>
      </span>
      <span className="CreepyButton-cover">{children}</span>
    </CreepyButtonRoot>
  );
}

